import { objectRecord, requiredString } from './dto';
export function base64urlToArrayBuffer(value) {
    if (!/^[A-Za-z0-9_-]*={0,2}$/.test(value))
        throw new Error('Invalid base64url');
    const text = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(text, char => char.charCodeAt(0)).buffer;
}
export function arrayBufferToBase64url(value) {
    return btoa(Array.from(new Uint8Array(value), byte => String.fromCharCode(byte)).join(''))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
// JSON is validated at this browser boundary; native parsers also validate it.
function descriptors(value) {
    if (value === undefined)
        return undefined;
    if (!Array.isArray(value))
        throw new Error('Invalid credential descriptors');
    return value.map(item => {
        const record = objectRecord(item, 'Credential descriptor');
        if (record.type !== 'public-key')
            throw new Error('Invalid credential type');
        return { ...record, type: 'public-key', id: base64urlToArrayBuffer(requiredString(record.id, 'Credential id')) };
    });
}
export function creationOptionsFromJSON(value) {
    const json = objectRecord(value, 'Creation options');
    const native = typeof PublicKeyCredential !== 'undefined' ? PublicKeyCredential : undefined;
    if (native?.parseCreationOptionsFromJSON)
        return native.parseCreationOptionsFromJSON(json);
    const user = objectRecord(json.user, 'User');
    const rp = objectRecord(json.rp, 'Relying party');
    if (!Array.isArray(json.pubKeyCredParams))
        throw new Error('Invalid credential parameters');
    return {
        ...json,
        challenge: base64urlToArrayBuffer(requiredString(json.challenge, 'Challenge')),
        user: { ...user, id: base64urlToArrayBuffer(requiredString(user.id, 'User id')), name: requiredString(user.name, 'User name'), displayName: requiredString(user.displayName, 'Display name') },
        rp: { ...rp, name: requiredString(rp.name, 'Relying party name') },
        pubKeyCredParams: json.pubKeyCredParams.map(value => {
            const param = objectRecord(value, 'Credential parameter');
            if (param.type !== 'public-key' || typeof param.alg !== 'number')
                throw new Error('Invalid credential parameter');
            return { type: 'public-key', alg: param.alg };
        }),
        excludeCredentials: descriptors(json.excludeCredentials),
    };
}
export function requestOptionsFromJSON(value) {
    const json = objectRecord(value, 'Request options');
    const native = typeof PublicKeyCredential !== 'undefined' ? PublicKeyCredential : undefined;
    if (native?.parseRequestOptionsFromJSON)
        return native.parseRequestOptionsFromJSON(json);
    return { ...json, challenge: base64urlToArrayBuffer(requiredString(json.challenge, 'Challenge')), ...(json.allowCredentials === undefined ? {} : { allowCredentials: descriptors(json.allowCredentials) }) };
}
export function credentialToJSON(credential) {
    const native = credential;
    if (native.toJSON)
        return native.toJSON();
    const response = credential.response;
    const result = {
        id: credential.id, rawId: arrayBufferToBase64url(credential.rawId), type: credential.type,
        response: { clientDataJSON: arrayBufferToBase64url(response.clientDataJSON) },
        clientExtensionResults: credential.getClientExtensionResults(),
        authenticatorAttachment: credential.authenticatorAttachment,
    };
    if ('attestationObject' in response) {
        const registration = response;
        result.response.attestationObject = arrayBufferToBase64url(registration.attestationObject);
        if (registration.getTransports)
            result.response.transports = registration.getTransports();
    }
    else {
        const assertion = response;
        result.response.authenticatorData = arrayBufferToBase64url(assertion.authenticatorData);
        result.response.signature = arrayBufferToBase64url(assertion.signature);
        result.response.userHandle = assertion.userHandle === null ? null : arrayBufferToBase64url(assertion.userHandle);
    }
    return result;
}
export async function isPasskeySupported() {
    try {
        return typeof PublicKeyCredential !== 'undefined' && await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=webauthn.js.map